import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AlertCircle, CheckCircle2, Search, X, RefreshCw, Upload, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { CustomModal, CustomButton } from '../../atoms';
import { useToastStore } from '@/shared/stores/toastStore';
import './ImportPreviewModal.css';

const parsePythonDict = (dictStr: string): Record<string, string> => {
    const errors: Record<string, string> = {};
    const regex = /(?:['"]([^'"]+)['"])\s*:\s*(?:['"]([^'"]+)['"]|\[\s*['"]([^'"]+)['"]\s*\])/g;
    let match;
    while ((match = regex.exec(dictStr)) !== null) {
        const key = match[1];
        const val = match[2] || match[3] || '';
        if (key) {
            errors[key.trim()] = val.trim();
        }
    }
    return errors;
};

const mapFieldToHeader = (
    fieldName: string,
    type: 'departments' | 'employees' | 'parents' | 'students',
    currentHeaders: string[]
): string | undefined => {
    const findHeader = (pattern: RegExp) => currentHeaders.find(h => pattern.test(h));
    const lowerField = fieldName.toLowerCase();

    if (type === 'departments') {
        if (lowerField === 'name') {
            return findHeader(/name|department/i);
        }
    } else if (type === 'employees') {
        if (lowerField === 'name') return findHeader(/name|full\s*name/i);
        if (lowerField === 'email') return findHeader(/email/i);
        if (lowerField === 'password') return findHeader(/password|pass/i);
        if (lowerField.includes('dept') || lowerField.includes('department')) return findHeader(/department|dept/i);
    } else if (type === 'parents') {
        if (lowerField === 'name' || lowerField === 'parent_name') return findHeader(/parent\s*name/i) || findHeader(/^name$/i);
        if (lowerField === 'email' || lowerField === 'parent_email') return findHeader(/email/i);
        if (lowerField === 'password' || lowerField === 'parent_password') return findHeader(/password/i);
        if (lowerField === 'student_name' || lowerField === 'student' || lowerField === 'child_name') return findHeader(/student\s*name|child\s*name/i);
        if (lowerField === 'class' || lowerField === 'class_name' || lowerField === 'grade') return findHeader(/class|grade/i);
    } else if (type === 'students') {
        if (lowerField === 'name' || lowerField === 'student_name' || lowerField === 'student') return findHeader(/student\s*name|student|child\s*name/i) || findHeader(/^name$/i);
        if (lowerField === 'class' || lowerField === 'class_name' || lowerField === 'grade') return findHeader(/class|grade/i);
        if (lowerField === 'section') return findHeader(/section/i);
        if (lowerField === 'roll_number' || lowerField === 'roll') return findHeader(/roll/i);
        if (lowerField === 'admission_number' || lowerField === 'admission') return findHeader(/admission/i);
        if (lowerField === 'dob' || lowerField === 'date_of_birth') return findHeader(/dob|birth/i);
        if (lowerField === 'parent_email' || lowerField === 'email') return findHeader(/parent\s*email/i) || findHeader(/email/i);
    }

    const match = currentHeaders.find(h => h.toLowerCase() === lowerField || h.toLowerCase().replace(/[^a-z0-9]/g, '') === lowerField.replace(/[^a-z0-9]/g, ''));
    if (match) return match;

    return currentHeaders.find(h => {
        const normalizedH = h.toLowerCase().replace(/[^a-z0-9]/g, '');
        const normalizedF = lowerField.replace(/[^a-z0-9]/g, '');
        return normalizedH.includes(normalizedF) || normalizedF.includes(normalizedH);
    });
};

interface ImportPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    file: File | null;
    type: 'departments' | 'employees' | 'parents' | 'students';
    onImport: (file: File) => Promise<{ success: boolean; error?: string; message?: string; data?: { success: number; failed: number; errors?: string[] } }>;
    onSuccess: () => void;
}

export const ImportPreviewModal: React.FC<ImportPreviewModalProps> = ({
    isOpen,
    onClose,
    file,
    type,
    onImport,
    onSuccess
}) => {
    const { showToast } = useToastStore();
    const [isLoading, setIsLoading] = useState(false);
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<Record<string, string>[]>([]);
    const [originalRows, setOriginalRows] = useState<Record<string, string>[]>([]);
    const [errors, setErrors] = useState<Record<string, string>[]>([]); // Array of error maps per row
    const [apiErrors, setApiErrors] = useState<Record<number, string>>({});
    const [apiCellErrors, setApiCellErrors] = useState<Record<number, Record<string, string>>>({});
    const [globalServerErrors, setGlobalServerErrors] = useState<string[]>([]);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);

    // Editing State
    const [editingCell, setEditingCell] = useState<{ rowIndex: number; colName: string } | null>(null);
    const [editValue, setEditValue] = useState('');
    const editInputRef = useRef<HTMLInputElement>(null);

    // Reset state helper
    const resetState = useCallback(() => {
        setHeaders([]);
        setRows([]);
        setOriginalRows([]);
        setErrors([]);
        setSearchQuery('');
        setShowOnlyErrors(false);
        setEditingCell(null);
        setApiErrors({});
        setApiCellErrors({});
        setGlobalServerErrors([]);
    }, []);

    const validateRow = useCallback((row: Record<string, string>, currentHeaders: string[]): Record<string, string> => {
        const rowErrors: Record<string, string> = {};
        const findHeader = (pattern: RegExp) => currentHeaders.find(h => pattern.test(h));

        if (type !== 'parents') {
            // For departments, employees, and students, all uploaded columns are mandatory dynamically.
            currentHeaders.forEach(h => {
                const val = row[h] || '';
                if (!val.trim()) {
                    rowErrors[h] = `${h} is required`;
                } else {
                    // Apply dynamic format/length validation based on header naming patterns
                    if (/name|department/i.test(h)) {
                        if (val.trim().length < 2) {
                            rowErrors[h] = `${h} must be at least 2 characters`;
                        }
                    } else if (/email/i.test(h)) {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(val.trim())) {
                            rowErrors[h] = 'Invalid email address';
                        }
                    } else if (/password|pass/i.test(h)) {
                        if (val.trim().length < 6) {
                            rowErrors[h] = 'Password must be at least 6 characters';
                        }
                    }
                }
            });
        } else {
            const parentNameHeader = findHeader(/parent\s*name/i) || findHeader(/^name$/i);
            const parentEmailHeader = findHeader(/email/i);
            const parentPasswordHeader = findHeader(/password/i);

            if (parentNameHeader) {
                const val = row[parentNameHeader] || '';
                if (val.trim() && val.trim().length < 2) {
                    rowErrors[parentNameHeader] = 'Parent Name must be at least 2 characters';
                }
            }

            if (parentEmailHeader) {
                const val = row[parentEmailHeader] || '';
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (val.trim() && !emailRegex.test(val.trim())) {
                    rowErrors[parentEmailHeader] = 'Invalid email address';
                }
            }

            if (parentPasswordHeader) {
                const val = row[parentPasswordHeader] || '';
                if (val.trim() && val.trim().length < 6) {
                    rowErrors[parentPasswordHeader] = 'Password must be at least 6 characters';
                }
            }
        }

        return rowErrors;
    }, [type]);

    const processParsedData = useCallback((parsedHeaders: string[], parsedRows: Record<string, string>[]) => {
        // Run validation on all rows
        const parsedErrors = parsedRows.map(row => validateRow(row, parsedHeaders));
        
        setHeaders(parsedHeaders);
        setRows(parsedRows);
        setOriginalRows(JSON.parse(JSON.stringify(parsedRows)));
        setErrors(parsedErrors);
        setIsLoading(false);
    }, [validateRow]);

    const parseFile = useCallback((fileToParse: File) => {
        setIsLoading(true);
        const fileExtension = fileToParse.name.split('.').pop()?.toLowerCase();

        try {
            if (fileExtension === 'csv') {
                Papa.parse(fileToParse, {
                    header: true,
                    skipEmptyLines: 'greedy',
                    complete: (results) => {
                        const rawFields = results.meta.fields || [];
                        const parsedHeaders = rawFields.map(h => String(h || '').trim()).filter(h => h !== '');
                        const parsedRows = (results.data as Record<string, unknown>[])
                            .map(row => {
                                const formatted: Record<string, string> = {};
                                parsedHeaders.forEach(h => {
                                    formatted[h] = String(row[h] ?? '').trim();
                                });
                                return formatted;
                            })
                            .filter(row => Object.values(row).some(val => val !== ''));
                        processParsedData(parsedHeaders, parsedRows);
                    },
                    error: (error) => {
                        showToast(`Failed to parse CSV: ${error.message}`, 'error');
                        setIsLoading(false);
                    }
                });
            } else if (['xlsx', 'xls'].includes(fileExtension || '')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e.target?.result as ArrayBuffer);
                        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
                        if (workbook.SheetNames.length === 0) {
                            throw new Error('Workbook contains no sheets.');
                        }
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];

                        // Format all date type cells to YYYY-MM-DD to preserve exact dates
                        for (const key in sheet) {
                            if (Object.prototype.hasOwnProperty.call(sheet, key)) {
                                const cell = sheet[key];
                                if (cell && cell.t === 'd' && cell.v instanceof Date && !isNaN(cell.v.getTime())) {
                                    const dateVal = cell.v;
                                    const yyyy = dateVal.getUTCFullYear();
                                    const mm = String(dateVal.getUTCMonth() + 1).padStart(2, '0');
                                    const dd = String(dateVal.getUTCDate()).padStart(2, '0');
                                    cell.w = `${yyyy}-${mm}-${dd}`;
                                }
                            }
                        }
                        
                        // Parse sheet into a 2D array to ensure exact mapping of columns & headers
                        const rows2D = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: false, dateNF: 'yyyy-mm-dd' });
                        if (rows2D.length === 0) {
                            throw new Error('The uploaded sheet is empty.');
                        }

                        // Filter headers and keep track of original column indexes
                        const parsedHeadersWithIndex = (rows2D[0] || [])
                            .map((h, idx) => ({ name: String(h || '').trim(), index: idx }))
                            .filter(item => item.name !== '');

                        const parsedHeaders = parsedHeadersWithIndex.map(item => item.name);

                        const parsedRows = rows2D.slice(1)
                            .map(row => {
                                const formatted: Record<string, string> = {};
                                parsedHeadersWithIndex.forEach(item => {
                                    formatted[item.name] = String(row[item.index] ?? '').trim();
                                });
                                return formatted;
                            })
                            .filter(row => Object.values(row).some(val => val !== ''));

                        processParsedData(parsedHeaders, parsedRows);
                    } catch (error: unknown) {
                        const err = error as Error;
                        showToast(`Failed to parse Excel: ${err.message}`, 'error');
                        setIsLoading(false);
                    }
                };
                reader.readAsArrayBuffer(fileToParse);
            } else {
                showToast('Unsupported file type', 'error');
                setIsLoading(false);
            }
        } catch (error: unknown) {
            const err = error as Error;
            showToast(`Error reading file: ${err.message}`, 'error');
            setIsLoading(false);
        }
    }, [showToast, processParsedData]);

    // Re-parse when file or type changes
    useEffect(() => {
        if (isOpen && file) {
            parseFile(file);
        } else {
            resetState();
        }
    }, [isOpen, file, parseFile, resetState]);

    // Auto-focus input when entering editing mode
    useEffect(() => {
        if (editingCell && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingCell]);

    // Handlers
    const handleCellClick = (rowIndex: number, colName: string, currentValue: string) => {
        setEditingCell({ rowIndex, colName });
        setEditValue(currentValue);
    };

    const saveCellEdit = () => {
        if (!editingCell) return;
        const { rowIndex, colName } = editingCell;
        const updatedRows = [...rows];
        updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            [colName]: editValue.trim()
        };

        // Revalidate this specific row
        const rowErrors = validateRow(updatedRows[rowIndex], headers);
        const updatedErrors = [...errors];
        updatedErrors[rowIndex] = rowErrors;

        // Clear API error for this row if it was corrected
        if (apiErrors[rowIndex]) {
            const updatedApiErrors = { ...apiErrors };
            delete updatedApiErrors[rowIndex];
            setApiErrors(updatedApiErrors);
        }
        if (apiCellErrors[rowIndex]) {
            const updatedApiCellErrors = { ...apiCellErrors };
            if (updatedApiCellErrors[rowIndex][colName]) {
                const newRowCellErrors = { ...updatedApiCellErrors[rowIndex] };
                delete newRowCellErrors[colName];
                if (Object.keys(newRowCellErrors).length === 0) {
                    delete updatedApiCellErrors[rowIndex];
                    if (apiErrors[rowIndex]) {
                        const updatedApiErrors = { ...apiErrors };
                        delete updatedApiErrors[rowIndex];
                        setApiErrors(updatedApiErrors);
                    }
                } else {
                    updatedApiCellErrors[rowIndex] = newRowCellErrors;
                }
                setApiCellErrors(updatedApiCellErrors);
            }
        }

        setRows(updatedRows);
        setErrors(updatedErrors);
        setEditingCell(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            saveCellEdit();
        } else if (e.key === 'Escape') {
            setEditingCell(null);
        }
    };

    const handleReset = () => {
        if (window.confirm('Are you sure you want to discard all your inline corrections?')) {
            setRows(JSON.parse(JSON.stringify(originalRows)));
            const revalidatedErrors = originalRows.map(row => validateRow(row, headers));
            setErrors(revalidatedErrors);
            setApiErrors({});
            setApiCellErrors({});
            setGlobalServerErrors([]);
            showToast('All cell edits have been reset to original values.', 'info');
        }
    };

    const handleImport = async () => {
        // Double-check if there are any remaining errors
        const hasErrors = errors.some(rowErr => Object.keys(rowErr).length > 0);
        if (hasErrors) {
            showToast('Please correct all validation errors before importing.', 'error');
            return;
        }

        setIsLoading(true);
        setGlobalServerErrors([]);
        try {
            // Convert data back to CSV Blob
            const csvString = Papa.unparse({
                fields: headers,
                data: rows.map(row => headers.map(h => row[h] || ''))
            });
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const finalFileName = (file?.name || 'import').replace(/\.[^/.]+$/, "") + "_corrected.csv";
            const finalFile = new File([blob], finalFileName, { type: 'text/csv' });

            const response = await onImport(finalFile);
            
            // Check success
            if (response.success || !response.error) {
                const data = response.data;
                if (data && (data.failed > 0 || (data.errors && data.errors.length > 0))) {
                    // Extract API errors per row
                    const newApiErrors: Record<number, string> = {};
                    const newApiCellErrors: Record<number, Record<string, string>> = {};
                    const unmappedErrors: string[] = [];
                    const failedIndices = new Set<number>();
                    
                    if (Array.isArray(data.errors)) {
                        data.errors.forEach((errStr: string) => {
                            // Match row number, e.g. "Row 3:"
                            const rowMatch = errStr.match(/Row\s+(\d+)/i);
                            if (rowMatch) {
                                const rowNum = parseInt(rowMatch[1], 10);
                                const rowIndex = rowNum - 2; // Row 1 is header, Row 2 is index 0
                                if (rowIndex >= 0 && rowIndex < rows.length) {
                                    failedIndices.add(rowIndex);
                                    newApiErrors[rowIndex] = errStr;

                                    // Parse python-style dict field errors or string-style errors
                                    const dictStartIndex = errStr.indexOf('{');
                                    const dictEndIndex = errStr.lastIndexOf('}');
                                    let parsedSpecificField = false;

                                    if (dictStartIndex !== -1 && dictEndIndex !== -1 && dictEndIndex > dictStartIndex) {
                                        const dictStr = errStr.substring(dictStartIndex, dictEndIndex + 1);
                                        const fieldErrors = parsePythonDict(dictStr);
                                        const cellErrors: Record<string, string> = {};
                                        
                                        Object.entries(fieldErrors).forEach(([fieldName, errMsg]) => {
                                            const headerName = mapFieldToHeader(fieldName, type, headers);
                                            if (headerName) {
                                                cellErrors[headerName] = errMsg;
                                            }
                                        });
                                        if (Object.keys(cellErrors).length > 0) {
                                            newApiCellErrors[rowIndex] = cellErrors;
                                            parsedSpecificField = true;
                                        }
                                    }

                                    if (!parsedSpecificField) {
                                        const cellErrors: Record<string, string> = {};
                                        const rowData = rows[rowIndex];
                                        let foundCellMatch = false;

                                        // Extract clean error message after HTTP status or prefix
                                        let cleanErrMsg = errStr.replace(/^Row\s+\d+:\s*/i, '').trim();
                                        const statusCodesMatch = cleanErrMsg.match(/(?:\d{3}):\s*(.*)$/);
                                        if (statusCodesMatch && statusCodesMatch[1]) {
                                            cleanErrMsg = statusCodesMatch[1].trim();
                                        }

                                        // 1. Match specific cell values (like emails/names) mentioned in the error
                                        for (const h of headers) {
                                            const cellVal = (rowData[h] || '').trim();
                                            if (cellVal && cellVal.length >= 3 && (cleanErrMsg.toLowerCase().includes(cellVal.toLowerCase()) || errStr.toLowerCase().includes(cellVal.toLowerCase()))) {
                                                cellErrors[h] = cleanErrMsg;
                                                foundCellMatch = true;
                                                break;
                                            }
                                        }

                                        // 2. If no cell matched but message mentions field types
                                        if (!foundCellMatch) {
                                            if (cleanErrMsg.toLowerCase().includes('email')) {
                                                const emailHeader = headers.find(h => /email/i.test(h));
                                                if (emailHeader) {
                                                    cellErrors[emailHeader] = cleanErrMsg;
                                                    foundCellMatch = true;
                                                }
                                            } else if (cleanErrMsg.toLowerCase().includes('name')) {
                                                const nameHeader = headers.find(h => /name|full\s*name/i.test(h));
                                                if (nameHeader) {
                                                    cellErrors[nameHeader] = cleanErrMsg;
                                                    foundCellMatch = true;
                                                }
                                            } else if (cleanErrMsg.toLowerCase().includes('department') || cleanErrMsg.toLowerCase().includes('dept')) {
                                                const deptHeader = headers.find(h => /department|dept/i.test(h));
                                                if (deptHeader) {
                                                    cellErrors[deptHeader] = cleanErrMsg;
                                                    foundCellMatch = true;
                                                }
                                            } else if (cleanErrMsg.toLowerCase().includes('password') || cleanErrMsg.toLowerCase().includes('pass')) {
                                                const passHeader = headers.find(h => /password|pass/i.test(h));
                                                if (passHeader) {
                                                    cellErrors[passHeader] = cleanErrMsg;
                                                    foundCellMatch = true;
                                                }
                                            }
                                        }

                                        if (Object.keys(cellErrors).length > 0) {
                                            newApiCellErrors[rowIndex] = cellErrors;
                                        }
                                    }
                                    return;
                                }
                            }
                            unmappedErrors.push(errStr);
                        });
                    } else {
                        unmappedErrors.push(response.message || 'Some rows failed to import.');
                    }
                    
                    // Filter the grid: KEEP only failed rows, discard succeeded rows
                    if (failedIndices.size > 0) {
                        const updatedRows: Record<string, string>[] = [];
                        const updatedErrors: Record<string, string>[] = [];
                        const updatedApiErrors: Record<number, string> = {};
                        const updatedApiCellErrors: Record<number, Record<string, string>> = {};
                        
                        let newIdx = 0;
                        rows.forEach((row, oldIdx) => {
                            if (failedIndices.has(oldIdx)) {
                                updatedRows.push(row);
                                updatedErrors.push(errors[oldIdx] || {});
                                if (newApiErrors[oldIdx]) {
                                    updatedApiErrors[newIdx] = newApiErrors[oldIdx];
                                }
                                if (newApiCellErrors[oldIdx]) {
                                    updatedApiCellErrors[newIdx] = newApiCellErrors[oldIdx];
                                }
                                newIdx++;
                            }
                        });
                        
                        setRows(updatedRows);
                        setErrors(updatedErrors);
                        setApiErrors(updatedApiErrors);
                        setApiCellErrors(updatedApiCellErrors);
                    } else {
                        setApiErrors(newApiErrors);
                        setApiCellErrors(newApiCellErrors);
                    }

                    setGlobalServerErrors(unmappedErrors);
                    const successCount = data.success ?? (rows.length - failedIndices.size);
                    if (successCount > 0) {
                        onSuccess();
                    }
                    showToast(`Imported ${successCount} rows successfully. ${data.failed} rows failed. Please correct failed fields inline.`, 'warning');
                } else {
                    showToast(response.message || 'Data imported successfully!', 'success');
                    onSuccess();
                    onClose();
                }
            } else {
                setGlobalServerErrors([response.error || 'Failed to import file on server.']);
                showToast(response.error || 'Failed to import file on server.', 'error');
            }
        } catch (error: unknown) {
            const err = error as Error;
            setGlobalServerErrors([err.message || 'An unexpected error occurred during upload.']);
            showToast(err.message || 'An unexpected error occurred during upload', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate total rows with errors
    const errorRowsCount = errors.filter((rowErr, idx) => Object.keys(rowErr).length > 0 || apiErrors[idx] || (apiCellErrors[idx] && Object.keys(apiCellErrors[idx]).length > 0)).length;

    // Filter rows based on search and error toggle
    const filteredRowData = rows
        .map((row, index) => ({ 
            row, 
            index, 
            rowErrors: errors[index], 
            apiError: apiErrors[index],
            cellApiErrors: apiCellErrors[index] || {}
        }))
        .filter(({ row, rowErrors, apiError, cellApiErrors }) => {
            // Error filter
            if (showOnlyErrors && Object.keys(rowErrors).length === 0 && !apiError && Object.keys(cellApiErrors).length === 0) {
                return false;
            }
            // Search query filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                return headers.some(h => String(row[h] || '').toLowerCase().includes(query));
            }
            return true;
        });

    const hasErrorsGlobal = errors.some((rowErr, idx) => Object.keys(rowErr).length > 0 || apiErrors[idx] || (apiCellErrors[idx] && Object.keys(apiCellErrors[idx]).length > 0));

    const footer = (
        <div className="import-preview-modal-footer">
            <div className="import-preview-summary-text">
                {hasErrorsGlobal ? (
                    <span className="text-warning">
                        <AlertCircle size={16} className="vertical-align-middle" />
                        {' '}{errorRowsCount} rows contain validation errors. Please edit cells to resolve them.
                    </span>
                ) : (
                    <span className="text-success">
                        <CheckCircle2 size={16} className="vertical-align-middle" />
                        {' '}All rows are valid. You are ready to import!
                    </span>
                )}
            </div>
            <div className="import-preview-actions">
                <CustomButton
                    variant="outline"
                    color="secondary"
                    onClick={handleReset}
                    disabled={isLoading || rows.length === 0}
                >
                    <RefreshCw size={14} style={{ marginRight: '0.4rem' }} />
                    Reset
                </CustomButton>
                <CustomButton
                    variant="outline"
                    color="secondary"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancel
                </CustomButton>
                <CustomButton
                    variant="filled"
                    color="primary"
                    onClick={handleImport}
                    loading={isLoading}
                    disabled={hasErrorsGlobal || rows.length === 0}
                >
                    <Upload size={14} style={{ marginRight: '0.4rem' }} />
                    Import Data
                </CustomButton>
            </div>
        </div>
    );

    return (
        <CustomModal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            title={`Import Preview - ${file?.name || 'Untitled File'}`}
            footer={footer}
            className="import-preview-modal-wrapper"
        >
            <div className="import-preview-container">
                {globalServerErrors.length > 0 && (
                    <div className="import-preview-global-errors">
                        <h4 className="global-errors-title">
                            <AlertCircle size={16} className="vertical-align-middle" style={{ marginRight: '0.4rem', color: '#ef4444' }} />
                            Server Import Errors / Warnings
                        </h4>
                        <ul className="global-errors-list">
                            {globalServerErrors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Search and filter bar */}
                <div className="import-preview-bar">
                    <div className="import-preview-search-wrapper">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search in preview..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="import-preview-search"
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery('')} className="clear-search">
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <div className="import-preview-toggles">
                        <label className="import-preview-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showOnlyErrors}
                                onChange={(e) => setShowOnlyErrors(e.target.checked)}
                                className="import-preview-checkbox"
                            />
                            Show only rows with errors ({errorRowsCount})
                        </label>
                    </div>
                </div>

                {/* Grid view */}
                <div className="import-preview-grid-wrapper">
                    {isLoading ? (
                        <div className="import-preview-loading">
                            <div className="loader"></div>
                            <p>Reading and validating file content...</p>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="import-preview-empty">
                            <FileSpreadsheet size={48} className="empty-icon" />
                            <p>No valid data parsed from file.</p>
                        </div>
                    ) : (
                        <table className="import-preview-table">
                            <thead>
                                <tr>
                                    <th className="column-status">Status</th>
                                    <th className="column-index">#</th>
                                    {headers.map(h => (
                                        <th key={h} className="column-header">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRowData.map(({ row, index, rowErrors, apiError, cellApiErrors }) => {
                                    const isRowInvalid = Object.keys(rowErrors).length > 0;
                                    const hasCellApiErrors = Object.keys(cellApiErrors).length > 0;
                                    const rowClassName = `preview-row ${isRowInvalid ? 'row-invalid' : ''} ${(apiError || hasCellApiErrors) ? 'row-api-invalid' : ''}`;
                                    return (
                                        <tr key={index} className={rowClassName}>
                                            <td className="cell-status">
                                                {(apiError || hasCellApiErrors) ? (
                                                    <span className="api-error-wrapper">
                                                        <AlertCircle size={18} className="status-icon-api-err" />
                                                        <span className="api-error-tooltip">
                                                            {apiError || Object.entries(cellApiErrors).map(([col, err]) => `${col}: ${err}`).join('\n')}
                                                        </span>
                                                    </span>
                                                ) : isRowInvalid ? (
                                                    <span title="Row has validation errors">
                                                        <AlertCircle size={18} className="status-icon-err" />
                                                    </span>
                                                ) : (
                                                    <span title="Row is valid">
                                                        <CheckCircle2 size={18} className="status-icon-ok" />
                                                    </span>
                                                )}
                                            </td>
                                            <td className="cell-index">{index + 1}</td>
                                            {headers.map(h => {
                                                const cellValue = row[h] || '';
                                                const cellError = rowErrors[h] || cellApiErrors[h];
                                                const isCellEditing = editingCell?.rowIndex === index && editingCell?.colName === h;

                                                return (
                                                    <td
                                                        key={h}
                                                        className={`preview-cell ${cellError ? 'cell-invalid' : ''} ${isCellEditing ? 'cell-editing' : ''}`}
                                                        onClick={() => handleCellClick(index, h, cellValue)}
                                                        title={cellError || 'Click to edit'}
                                                    >
                                                        {isCellEditing ? (
                                                            <input
                                                                ref={editInputRef}
                                                                type="text"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={saveCellEdit}
                                                                onKeyDown={handleKeyDown}
                                                                className="cell-edit-input"
                                                            />
                                                        ) : (
                                                            <div className="cell-value-container">
                                                                <span className="cell-text-value">
                                                                    {cellValue || <span className="cell-placeholder">[Empty]</span>}
                                                                </span>
                                                                {cellError && (
                                                                    <div className="cell-error-badge">
                                                                        {cellError}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </CustomModal>
    );
};

export default ImportPreviewModal;
